import { validatePatentFormat } from '../validate-patent-format'

export interface DocumentNumberValidationResult {
  /** List of errors found during validation */
  errors: string[]
  /** Document numbers that have been validated by the server and are formatted correctly */
  validDocumentNumbers: string[]
  /** Document numbers that were already in the current list */
  duplicateDocumentNumbers: string[]
}

export interface ValidateDocumentNumbersOptions {
  /** Maximum allowed document numbers */
  maxAllowed?: number
  /** Current document numbers that already exist */
  existingDocumentNumbers?: string[]
}

/**
 * Validates a list of document numbers
 * @param documentNumbers - Array of document numbers to validate
 * @param validateDocumentNumbersCallback - Function to validate document numbers via API
 * @param options - Configuration options
 * @returns Validation result with errors, valid numbers, and duplicates
 */
export async function validateDocumentNumbers(
  documentNumbers: string[],
  validateDocumentNumbersCallback: (documentNumbers: string[]) => Promise<{
    validated_document_numbers: Array<string | null>
  }>,
  options: ValidateDocumentNumbersOptions = {}
): Promise<DocumentNumberValidationResult> {
  const { maxAllowed = 10_000, existingDocumentNumbers = [] } = options

  const errors: string[] = []
  const validDocumentNumbers: string[] = []
  const duplicateDocumentNumbers: string[] = []
  const formatErrors: string[] = []

  for (const documentNumber of documentNumbers) {
    if (!documentNumber) {
      continue
    }

    if (!validatePatentFormat(documentNumber)) {
      formatErrors.push(documentNumber)
      continue
    }

    if (
      existingDocumentNumbers.includes(documentNumber) ||
      validDocumentNumbers.includes(documentNumber)
    ) {
      if (!duplicateDocumentNumbers.includes(documentNumber)) {
        duplicateDocumentNumbers.push(documentNumber)
      }
      continue
    }

    validDocumentNumbers.push(documentNumber)
  }

  if (formatErrors.length > 0) {
    const firstThree = formatErrors.slice(0, 3)
    let formatErrorMessage = `Invalid format for: ${firstThree.join(', ')}`
    if (formatErrors.length > 3) {
      formatErrorMessage += ` (and ${formatErrors.length - 3} more invalid formats).`
    }
    errors.push(formatErrorMessage)
  }

  if (
    existingDocumentNumbers.length + validDocumentNumbers.length >
    maxAllowed
  ) {
    errors.push(
      `Maximum number of document numbers (${maxAllowed}) would be exceeded. Found ${validDocumentNumbers.length} new unique patents, but only ${maxAllowed - existingDocumentNumbers.length} more can be added.`
    )
    return { errors, validDocumentNumbers: [], duplicateDocumentNumbers }
  }

  if (validDocumentNumbers.length > 0) {
    try {
      const validationResult =
        await validateDocumentNumbersCallback(validDocumentNumbers)
      const validatedNumbersFromApi =
        validationResult.validated_document_numbers

      const serverValidatedNumbers: string[] = []
      const invalidNumbersOriginalFormat: string[] = []

      for (const [
        index,
        validatedNumber,
      ] of validatedNumbersFromApi.entries()) {
        if (validatedNumber === null) {
          const originalNumber = validDocumentNumbers[index]
          if (originalNumber) {
            invalidNumbersOriginalFormat.push(originalNumber)
          }
        } else {
          serverValidatedNumbers.push(validatedNumber)
        }
      }

      if (invalidNumbersOriginalFormat.length > 0) {
        const invalidServerErrorMessages = invalidNumbersOriginalFormat
          .slice(0, 3)
          .map((number) => number)
        let serverErrorMessage = `Invalid patent numbers found: ${invalidServerErrorMessages.join(', ')}`
        if (invalidNumbersOriginalFormat.length > 3) {
          serverErrorMessage += ` (and ${invalidNumbersOriginalFormat.length - 3} more invalid patents).`
        }
        errors.push(serverErrorMessage)

        return {
          errors,
          validDocumentNumbers: serverValidatedNumbers,
          duplicateDocumentNumbers,
        }
      }

      return {
        errors,
        validDocumentNumbers: serverValidatedNumbers,
        duplicateDocumentNumbers,
      }
    } catch {
      errors.push('Failed to validate patent numbers via API')
      return { errors, validDocumentNumbers: [], duplicateDocumentNumbers }
    }
  }

  if (
    duplicateDocumentNumbers.length > 0 &&
    validDocumentNumbers.length === 0 &&
    errors.length === 0
  ) {
    errors.push(
      `All ${duplicateDocumentNumbers.length} patent numbers were already in the list.`
    )
  }

  return { errors, validDocumentNumbers, duplicateDocumentNumbers }
}

/**
 * Process CSV data to validate and extract document numbers
 * @param csvData - 2D array of CSV rows and columns
 * @param validateDocumentNumbersCallback - Function to validate document numbers via API
 * @param options - Configuration options
 * @returns Validation result with errors, valid numbers, and duplicates
 */
export async function processDocumentNumbersCsv(
  csvData: string[][],
  validateDocumentNumbersCallback: (documentNumbers: string[]) => Promise<{
    validated_document_numbers: Array<string | null>
  }>,
  options: ValidateDocumentNumbersOptions = {}
): Promise<DocumentNumberValidationResult> {
  const documentNumbers: string[] = []

  for (const [index, row] of csvData.entries()) {
    const documentNumber = row[0]
    if (!documentNumber) {
      continue
    }

    if (index === 0 && !validatePatentFormat(documentNumber)) {
      continue
    }

    documentNumbers.push(documentNumber)
  }

  return validateDocumentNumbers(
    documentNumbers,
    validateDocumentNumbersCallback,
    options
  )
}
